import { useEffect, useRef, useState } from 'react'

interface IntersectionObserverHook {
  callback: (entry: IntersectionObserverEntry) => void
  root?: Document | Element | null
  rootMargin?: string
  threshold?: number[] | number
}

export const useIntersectionObserver = ({
  callback,
  root,
  rootMargin = '0px',
  threshold,
}: IntersectionObserverHook) => {
  const [node, setNode] = useState<Element | null>()
  const observer = useRef<IntersectionObserver>()

  useEffect(() => {
    if (observer.current) {
      observer.current.disconnect()
    }

    const onIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => entry.isIntersecting && callback(entry))
    }

    observer.current = new IntersectionObserver(onIntersection, {
      root,
      rootMargin,
      threshold,
    })

    if (node) {
      observer.current.observe(node)
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect()
      }
    }
  }, [node, root, rootMargin, threshold, callback])

  return {
    setObservedNode: setNode,
  }
}
